import { useFeatureAvailable, useSoftDeployAccess } from 'App/components/hooks';
import { ClientIdNamePriceList } from 'App/resources/Model/Client';
import { CurrencyFormat } from 'App/babel/utils/numberFormat';
import { Feature } from 'Store/actions/FeatureHelperActions';
import { hasSoftDeployAccess } from 'App/services/FeatureHelperStandalone';
import { PrimaryButton } from '@upsales/components/Buttons';
import { ContactIdName } from 'App/resources/Model/Contact';
import { useMetadata } from 'App/components/hooks/appHooks';
import BemClass from '@upsales/components/Utils/bemClass';
import { OldOrNewAgreement } from '../SubscriptionCards';
import { Text, Toggle } from '@upsales/components';
import T from 'Components/Helpers/translate';
import openModal from 'App/services/Modal';
import moment from 'moment';
import React from 'react';

import './CardsHeader.scss';
import AppService from 'App/legacy/AppService';

type Props = {
	showInactive: boolean;
	setShowInactive: (value: boolean | ((prevValue: boolean) => boolean)) => void;
	client?: ClientIdNamePriceList;
	contact?: ContactIdName;
	agreementGroups: OldOrNewAgreement[];
	hasMRR: boolean;
	groupCompanyToggle?: React.ReactNode;
};

const createSubscription = (client?: ClientIdNamePriceList, contact?: ContactIdName) => {
	const hasSubscriptionModal = hasSoftDeployAccess('SUBSCRIPTION_MODAL');
	if (!hasSubscriptionModal) {
		const options = { accountId: client?.id, contact, customerId: AppService.getCustomerId() };
		return Tools.$upModal.open('editAgreement', options);
	}

	openModal('CreateSubscription', { client, contact, dontWait: true, createdFrom: 'accountSalesAddButton' });
};

const CardsHeader = ({
	showInactive,
	setShowInactive,
	client,
	contact,
	agreementGroups,
	hasMRR,
	groupCompanyToggle
}: Props) => {
	const classes = new BemClass('CardsHeader');
	const hasSubAccountsV2 = useSoftDeployAccess('SUB_ACCOUNTS_V2');
	const hasSubaccountsFeature = useFeatureAvailable(Feature.SUB_ACCOUNTS) && hasSubAccountsV2;
	const hasClientRecurringOrdersFeature = useFeatureAvailable(Feature.CLIENT_RECURRING_ORDERS);
	const hasRecurringOrderFeature = useFeatureAvailable(Feature.RECURRING_ORDER);
	const hideNewAgreementButton = hasClientRecurringOrdersFeature && !hasRecurringOrderFeature;
	const { customerCurrencies } = useMetadata() ?? { customerCurrencies: [] };
	const masterCurrency = customerCurrencies.find(currency => currency.masterCurrency);

	const totalSum = agreementGroups.reduce((sum, current) => {
		const agreement = current.currentAgreement;
		const currentEndDate = agreement?.metadata.agreementEnddate;
		const startDate = agreement?.metadata.agreementStartdate;
		const isInactive = (!!currentEndDate && moment(currentEndDate).isBefore()) || moment(startDate).isAfter();

		if (!agreement || isInactive) return sum;

		const valueInMasterCurrency = (agreement.yearlyValueInMasterCurrency ?? 0) / (hasMRR ? 12 : 1);
		return sum + valueInMasterCurrency;
	}, 0);

	const showToggle = agreementGroups.length > 0;
	const currencyFormat = new CurrencyFormat(masterCurrency?.iso, undefined, 0);
	const value = `${currencyFormat.short(totalSum)} (${hasMRR ? 'MRR' : 'ARR'})`;

	return (
		<div className={classes.b()}>
			<div className={classes.elem('leftSide').b()}>
				<Text size="xl" bold>
					{T('default.agreement')}
					<Text color="grey-10" size="xl">
						{value}
					</Text>
				</Text>
			</div>
			<div className={classes.elem('rightSide').b()}>
				{showToggle ? (
					<>
						<Toggle checked={showInactive} onChange={() => setShowInactive(active => !active)}></Toggle>
						<Text>{`${T('default.show')} ${T('default.agreementsInactive').toLowerCase()}`}</Text>
					</>
				) : null}
				{hasSubaccountsFeature && showToggle ? groupCompanyToggle : null}
				{hideNewAgreementButton ? null : (
					<PrimaryButton icon="plus" onClick={() => createSubscription(client)}>
						{T('agreementHistory.typeNewAgreement')}
					</PrimaryButton>
				)}
			</div>
		</div>
	);
};

export default CardsHeader;
